/*
  The DataPath class is an abstract class (cannot be instantiated) and contains static methods for handling, verifying, and otherwise manipulating data paths in objects.
  Note: functions other than isValidPath() do NOT check for valid paths; we intentionally keep them thin for performance and defer to the use of isValidPath() when necessary
*/
export class DataPath {
    // returns true if the path is a valid path into a javascript object
    static isValidPath(path) {
        return DataPath.VALID_PATH_REGEX.test(path);
    }
    // returns true if path represents a parent path of childPath
    static isParentPath(path, childPath) {
        // check if path is a parent path of childPath. examples: childPath = a.b.c, path = a  |  childPath = a.b[1].c, path = a.b
        const childPathSnippet = childPath.substr(0, path.length + 1);
        // path is a parent of childPath if path is empty (top level object) and childPath is not OR if childPath is "under" path as an object OR an array
        return ((path === "" && childPath !== "") || childPathSnippet === path + "." || childPathSnippet === path + "[");
    }
    // returns true if path represents an array index path
    static isArrayIndexPath(path) {
        // return false if match returns null, true otherwise
        return (path.substr(path.length - 1) === "]");
    }
    // parses an array index path such as a.b[0][1] into the array name part "a.b" and array of dimensional indexes [0,1]
    static parseArrayIndexPath(path) {
        // run the regex and capture the matches
        const matches = path.match(DataPath.ARRAY_INDEX_PATH_REGEX);
        // if we didn't match an array index format throw an error
        if (matches === null) {
            throw new Error("Error: Not an array index path. The provided path '" + path + "' was not an array index path.");
        }
        // pick out the array path part (ex. a.b[0][1] => matches[1] = "a.b")
        const arrayPath = matches[1];
        // pick out the array indexes part (ex. a.b[0][1] => matches[2] = "[0][1]")
        const arrayIndexesString = matches[2];
        // generate an array of the indexes from the parsed string representation (ex. "[0][1]" => [0,1]): 1. rip off the leading '[' and trailing ']', 2. split on '][' into an array, 3. map the string elements to numbers (by passing the Number function to the map function).
        const dimensionalIndexes = arrayIndexesString.substr(1, arrayIndexesString.length - 2).split("][").map(Number);
        // return an object containing the arrayPath part and dimensionalIndexes as an array
        return { arrayPath: arrayPath, dimensionalIndexes: dimensionalIndexes };
    }
    // returns the part of childPath relative to parentPath
    static getRelativePath(parentPath, childPath) {
        // throw an error if childPath is NOT strictly a child path of parentPath
        if (!DataPath.isParentPath(parentPath, childPath)) {
            throw new Error("Error: No relative path relationship. Path '" + childPath + "' is not a child path of '" + parentPath + "'.");
        }
        // an empty parent path refers to the whole object; it's the parent of any non-trivial child path (the call to isParentPath ensures that childPath is a strict child path of, and not equal to, parentPath)
        if (parentPath === "") {
            return childPath;
        }
        // let's first assume that child path is a strict array index into the parent path (i.e. no period trailing the parent path to pluck off).  ex. parent path a.b with child path a.b[0]  OR  parent a.b[0] with child a.b[0][1]
        const candidateRelativePath = childPath.substr(parentPath.length);
        // if we indeed have a period to strip, do so!
        return ((candidateRelativePath.substr(0, 1) === ".") ? candidateRelativePath.substr(1) : candidateRelativePath);
    }
    // returns true if path represents a parent path of childPath
    static stripPath(path) {
        // find the last index of '.'
        const lastPeriodIndex = path.lastIndexOf(".");
        // will properly return "" and "a" in the case that '.' is not found (i.e. lastPeriodIndex = -1)
        return { basePath: path.substr(0, lastPeriodIndex), leaf: path.substr(lastPeriodIndex + 1) };
    }
}
// regular expression which matches if a path is a valid path into a javascript object
DataPath.VALID_PATH_REGEX = /^[^\[\]\.]+(?:\[\d+\])*(\.[^\[\]\.]+(?:\[\d+\])*)*$|^$/;
// regular expression which matches if a path is an array index path
DataPath.ARRAY_INDEX_PATH_REGEX = /^(.+?)((?:\[\d+\])+)$/;
//# sourceMappingURL=DataPath.js.map